import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Skeleton,
  Chip,
  Drawer,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/axios";

const ProductQnA = ({ productId, productVendorId }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [question, setQuestion] = useState("");
  const [posting, setPosting] = useState(false);

  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [answersLoading, setAnswersLoading] = useState(false);

  const [answerText, setAnswerText] = useState("");
  const [postingAnswer, setPostingAnswer] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));

  const isVendor = user?.role === "vendor";
  const isProductOwner = isVendor && productVendorId &&
    (user?._id === productVendorId || user?.vendorId === productVendorId);

  /* ================= LOAD QUESTIONS ================= */
  const loadQuestions = async () => {
    try {
      const res = await api.get(
        `/products/products/${productId}/questions`
      );
      setQuestions(res.data || []);
    } catch (err) {
      console.error("QnA load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) loadQuestions();
  }, [productId]);

  /* ================= ASK QUESTION ================= */
  const handleAsk = async () => {
    if (!question.trim()) return;

    if (!user) {
      alert("Please login to ask a question");
      return;
    }

    try {
      setPosting(true);

      await api.post(
        `/products/products/${productId}/questions`,
        { questionText: question }
      );

      setQuestion("");
      setSuggestions([]);
      await loadQuestions();
    } catch (err) {
      console.error("Ask failed", err);
    } finally {
      setPosting(false);
    }
  };

  /* ================= SIMILAR QUESTIONS ================= */
  const handleQuestionChange = (val) => {
    setQuestion(val);

    if (val.length > 3) {
      const matches = questions.filter((q) =>
        q.questionText.toLowerCase().includes(val.toLowerCase())
      );
      setSuggestions(matches.slice(0, 3));
    } else {
      setSuggestions([]);
    }
  };

  /* ================= LOAD ANSWERS ================= */
  const openAnswers = async (q) => {
    try {
      setSelectedQuestion(q);
      setAnswersLoading(true);

      const res = await api.get(
        `/products/questions/${q._id}/answers`
      );
      setAnswers(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setAnswersLoading(false);
    }
  };

  /* ================= POST ANSWER ================= */
  const handleAnswer = async () => {
    if (!answerText.trim()) return;

    if (!user) {
      alert("Please login to answer");
      return;
    }

    try {
      setPostingAnswer(true);

      await api.post(
        `/products/questions/${selectedQuestion._id}/answers`,
        { answerText }
      );

      setAnswerText("");

      const res = await api.get(
        `/products/questions/${selectedQuestion._id}/answers`
      );
      setAnswers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setPostingAnswer(false);
    }
  };

  /* ================= VOTING ================= */
  const handleVote = async (entityId, entityType, voteType) => {
    try {
      await api.post(`/products/vote`, {
        entityId,
        entityType,
        voteType,
      });

      setQuestions((prev) =>
        prev.map((q) =>
          q._id === entityId
            ? {
                ...q,
                upvotes:
                  voteType === "upvote"
                    ? q.upvotes + 1
                    : q.upvotes,
                downvotes:
                  voteType === "downvote"
                    ? q.downvotes + 1
                    : q.downvotes,
              }
            : q
        )
      );
    } catch (err) {
      console.error("Vote failed", err);
    }
  };

  /* ================= FILTER ================= */
  const filteredQuestions = questions.filter((q) =>
    q.questionText.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        <Typography fontWeight={700} mb={2}>
          Questions & Answers
        </Typography>

        {/* SEARCH */}
        <TextField
          fullWidth
          placeholder="Search questions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* ASK BOX */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Have a question? Ask here..."
            value={question}
            onChange={(e) =>
              handleQuestionChange(e.target.value)
            }
          />

          {/* SUGGESTIONS */}
          {suggestions.length > 0 && (
            <Paper sx={{ mt: 1, p: 1 }}>
              <Typography variant="caption">
                Similar questions:
              </Typography>

              {suggestions.map((s) => (
                <Typography
                  key={s._id}
                  sx={{
                    fontSize: 13,
                    cursor: "pointer",
                    color: "#007185",
                  }}
                  onClick={() => setQuestion(s.questionText)}
                >
                  {s.questionText}
                </Typography>
              ))}
            </Paper>
          )}

          <Button
            variant="contained"
            sx={{ mt: 1 }}
            disabled={!question.trim() || posting}
            onClick={handleAsk}
          >
            {posting ? "Posting..." : "Ask Question"}
          </Button>
        </Box>

        {/* CONTENT */}
        {loading ? (
          <>
            <Skeleton height={40} />
            <Skeleton height={40} />
          </>
        ) : filteredQuestions.length === 0 ? (
          <Typography color="text.secondary">
            No questions found
          </Typography>
        ) : (
          filteredQuestions.map((q) => (
            <Box
              key={q._id}
              sx={{
                mb: 3,
                pb: 2,
                borderBottom: "1px solid #eee",
              }}
            >
              <Typography fontWeight={600}>
                Q: {q.questionText}
              </Typography>

              {q.topAnswer && (
                <Box sx={{ mt: 1, bgcolor: "#f6f6f6", p: 1 }}>
                  <Typography>
                    A: {q.topAnswer.answerText}
                  </Typography>

                  {q.topAnswer.isVendorAnswer && (
                    <Chip
                      label="Answered by Seller"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    handleVote(q._id, "question", "upvote")
                  }
                >
                  <ThumbUpIcon fontSize="small" />
                  <Typography sx={{ ml: 0.5 }}>
                    {q.upvotes}
                  </Typography>
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() =>
                    handleVote(q._id, "question", "downvote")
                  }
                >
                  <ThumbDownIcon fontSize="small" />
                  <Typography sx={{ ml: 0.5 }}>
                    {q.downvotes}
                  </Typography>
                </IconButton>

                {!(isVendor && !isProductOwner) && (
                  <Button
                    size="small"
                    onClick={() => openAnswers(q)}
                  >
                    See all {q.answerCount} answers
                  </Button>
                )}
              </Stack>
            </Box>
          ))
        )}
      </Paper>

      {/* DRAWER */}
      <Drawer
        anchor="right"
        open={!!selectedQuestion}
        onClose={() => setSelectedQuestion(null)}
      >
        <Box sx={{ width: 400, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography fontWeight={700}>
              Answers
            </Typography>

            <IconButton
              onClick={() => setSelectedQuestion(null)}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 2 }} />

          {selectedQuestion && (
            <Typography fontWeight={600}>
              Q: {selectedQuestion.questionText}
            </Typography>
          )}

          {/* ANSWER INPUT */}
          {!(isVendor && !isProductOwner) && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                placeholder="Write your answer..."
                value={answerText}
                onChange={(e) =>
                  setAnswerText(e.target.value)
                }
              />

              <Button
                variant="contained"
                sx={{ mt: 1 }}
                disabled={!answerText.trim() || postingAnswer}
                onClick={handleAnswer}
              >
                {postingAnswer
                  ? "Posting..."
                  : "Post Answer"}
              </Button>
            </Box>
          )}

          {/* ANSWERS */}
          {answersLoading ? (
            <Skeleton height={40} />
          ) : answers.length === 0 ? (
            <Typography sx={{ mt: 2 }}>
              No answers yet
            </Typography>
          ) : (
            answers.map((a) => (
              <Box
                key={a._id}
                sx={{
                  mt: 2,
                  pb: 2,
                  borderBottom: "1px solid #eee",
                }}
              >
                <Typography>{a.answerText}</Typography>

                {a.isVendorAnswer && (
                  <Chip
                    label="Seller"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
            ))
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default ProductQnA;